// E08-t2 — THE DASHBOARDS PAGE.
//
// Dashboards.md f1: "list the KB's definition pairs with render status and
//   stable URLs", and the overview: "the assembled composite — grouped per
//   FORMAT, since a KB may hold several — is what renders". So the page's top
//   level structure is the FORMAT, not the dashboard: kb_record holds several
//   formats over one corpus and 0015 says "each format owns its
//   dashboards/composites and their views".
// Dashboards.md f2: a published pair is SERVED from R2 at a stable URL, so the
//   list LINKS to git-host/src/serve.ts SERVE_ROUTE and never re-renders
//   anything in-app. The url is the control plane's own string; this file does
//   not assemble it.
// Dashboards.md f3 + Dashboard On Demand f1/f3: asking for a new view is a
//   HANDOFF. The mission is the INTENT and the AUDIENCE; the agent drafts; a
//   maintainer accepts. This page therefore calls `onRequestView` and lands
//   nothing — no generation here, no second generator.
// Forge f2: NO free-form editor anywhere. The only write this page offers is
//   picking and ORDERING stored views (0015: "a composition is a versioned list
//   of view references"), which is a list of ids — never YAML, never text.
// platform/src/guards.ts: ROLE_CAPABILITIES gives a viewer ['read','capture'].
//   Composing and requesting are writes, so a viewer is offered NEITHER — and
//   the control is ABSENT rather than disabled, for the same reason the shell
//   omits the Admin link: nobody is walked to a door that will be shut.
//   Hiding is honesty, not enforcement; the Worker refuses regardless.
// E14-t5: at the current tier NOBODY is offered those two writes — the request
//   handoff IS generation and composing is the other half of it. `showAgentSurfaces`
//   (src/tier.ts, passed by src/App.tsx) takes them away for every role, and what
//   is left is what this tier sells: the published pairs, their render status and
//   their stable urls. The controls are absent, not disabled, for the same reason
//   as above.
//
// The page is a pure function of (props, its own draft state). It never calls
// fetch: the transport is injected (src/api/dashboards.ts), which is what lets
// the component contract run with no network at all.
import { useEffect, useState } from 'react';

import { Badge } from '@astryxdesign/core/Badge';
import { Button } from '@astryxdesign/core/Button';
import { Card } from '@astryxdesign/core/Card';
import { Divider } from '@astryxdesign/core/Divider';
import { Heading } from '@astryxdesign/core/Heading';
import { Link } from '@astryxdesign/core/Link';
import { Stack } from '@astryxdesign/core/Stack';
import { Text } from '@astryxdesign/core/Text';
import { TextInput } from '@astryxdesign/core/TextInput';

import type {
  Composition,
  DashboardsClient,
  FormatDashboards,
  KbDashboards,
  RenderStatus,
  RequestViewHandoff,
} from '../api/dashboards';
import { Chip, type ChipState } from '../components/Chip';
// E14 t3 left this page with a PRIVATE copy of `day`, and units.ts's own comment
// named it: "src/pages/Dashboards.tsx still holds a private copy of this". Two
// functions formatting one timestamp is exactly the drift this product exists to
// catch, so the page reads the shared one.
import { isoDay } from '../format/units';
import type { Role } from '../shell/AppShell';

import './dashboards.css';

// E14-t6 — the chip, the status pill, the four button variants, the two text
// fields and the card-per-format were all hand-built here out of tokens. They are
// now Badge, Button, TextInput, Card, Heading, Text and Link.
//
// ONE STATUS GAINED A COLOUR IT NEVER HAD. The old skin styled `.dj-status`
// generically and gave real colour to `rendered` and `failed` ONLY — `stale` and
// `never` fell through to the neutral grey, so "Stale" and "Render failed" looked
// equally unremarkable. Mapping the vocabulary onto Badge variants (below) is what
// the design system is for: four statuses, four readings.
//
// E15-t3 MOVED THAT MAPPING OFF BADGE AND ONTO THE FORMAT'S OWN VOCABULARY. The
// four readings stayed four; what changed is that they are now built / drift /
// unspecified / missing (STATUS_CHIP, below) rather than success / warning /
// neutral / error, so a render status is drawn in the same five-state palette
// as every other state in the product — and `stale` stopped borrowing amber for
// what is plainly drift. `Badge` remains here for "Primary", which marks a
// format rather than reporting a state.

export interface DashboardsPageProps {
  /** orgs.name — the :org path segment */
  org: string;
  /** kbs.name — the :kb path segment */
  kb: string;
  /** the actor's role IN THIS KB (platform/src/guards.ts) */
  role: Role;
  client: DashboardsClient;
  /** Dashboards f3 → Agent Chat f3: the page hands the request off */
  onRequestView: (handoff: RequestViewHandoff) => void;
  /** E14-t5 — THE TIER GATE. `false` removes the two WRITE surfaces below —
   *  "Request a view" (the handoff into generation) and "Compose" with its
   *  stored-view picker, its order controls and "Save composition" — leaving the
   *  page as what this tier does sell: the published pairs, their render status
   *  and their stable urls. Absent, not disabled: src/tier.ts says why, and it
   *  is the ONE place the decision lives. The default keeps this component
   *  saying what E08-t2 built it to say. */
  showAgentSurfaces?: boolean;
}

/** The render-status vocabulary, verbatim. A status the reader cannot name is a
 *  status the reader cannot act on. */
const STATUS_LABEL: Record<RenderStatus, string> = {
  rendered: 'Rendered',
  stale: 'Stale',
  never: 'Not rendered yet',
  failed: 'Render failed',
};

/**
 * E15-t3 — THE SAME FOUR STATUSES, IN THE FORMAT'S OWN STATE PALETTE. Kept
 * beside STATUS_LABEL, as the Badge variants were, so a status still cannot
 * gain a word without gaining a colour — and all four remain distinct, which is
 * the property that made this table worth writing in the first place.
 *
 * ONE OF THE FOUR CHANGED HUE, and it is a correction rather than a re-skin.
 * `stale` was amber, because Astryx's four semantic variants are the only ones
 * that exist and amber was the nearest. It is DRIFT: a render that no longer
 * matches the commit it was computed from is the exact case format.yaml spends
 * `info` on — evidence that has moved out from under the claim. The chip
 * vocabulary has that word, so `stale` now reads in the drift blue and stops
 * borrowing `partial`'s meaning.
 *
 *   rendered → built        published and current
 *   stale    → drift        published, and the source moved underneath it
 *   never    → unspecified  no answer yet, which is not a fault
 *   failed   → missing      it was attempted and there is nothing to open
 */
export const STATUS_CHIP: Record<RenderStatus, ChipState> = {
  rendered: 'built',
  stale: 'drift',
  never: 'unspecified',
  failed: 'missing',
};

/** A composition is addressed by (format, id) — ids are unique per format. */
const keyOf = (format: string, composition: string) => `${format}::${composition}`;

/** ISO in, a stable day out. Deliberately not locale-formatted: the same render
 *  must read the same in every browser and in every test. */
type Load =
  | { phase: 'loading' }
  | { phase: 'ready'; data: KbDashboards }
  | { phase: 'error'; message: string };

interface RequestDraft {
  open: boolean;
  intent: string;
  audience: string;
}

const NO_REQUEST: RequestDraft = { open: false, intent: '', audience: '' };

export function DashboardsPage({
  org,
  kb,
  role,
  client,
  onRequestView,
  showAgentSurfaces = true,
}: DashboardsPageProps) {
  const [load, setLoad] = useState<Load>({ phase: 'loading' });
  /** which composition surfaces are open, by (format, composition) */
  const [open, setOpen] = useState<Record<string, boolean>>({});
  /** the ORDERED view ids a maintainer is working on, by (format, composition).
   *  Seeded from the stored composition when the surface is opened, so an
   *  unopened composition is never quietly re-saved. */
  const [order, setOrder] = useState<Record<string, string[]>>({});
  /** the request-a-view form, per format */
  const [requests, setRequests] = useState<Record<string, RequestDraft>>({});

  useEffect(() => {
    let live = true;
    client.listDashboards(org, kb).then(
      (data) => {
        if (live) setLoad({ phase: 'ready', data });
      },
      (err: unknown) => {
        if (live) setLoad({ phase: 'error', message: (err as Error)?.message ?? String(err) });
      },
    );
    return () => {
      live = false;
    };
  }, [client, org, kb]);

  // guards.ts: 'command' belongs to admin and maintainer. Composing and asking
  // for a new pair are writes; reading the list is not.
  //
  // E14-t5 ANDs a second condition onto the same flag, because both surfaces it
  // gates are exactly these two writes. The two reasons for hiding are NOT the
  // same and the distinction is worth keeping straight: the role check is what
  // the guard would refuse, the tier check is what this product does not sell
  // yet. Neither is enforcement — platform/src/guards.ts refuses regardless.
  const canWrite = showAgentSurfaces && (role === 'admin' || role === 'maintainer');

  function toggleCompose(format: string, composition: Composition) {
    const key = keyOf(format, composition.id);
    setOrder((current) => (key in current ? current : { ...current, [key]: composition.views }));
    setOpen((current) => ({ ...current, [key]: !current[key] }));
  }

  /** Checking a stored view APPENDS it to the end of the composition order. */
  function toggleView(key: string, viewId: string) {
    setOrder((current) => {
      const chosen = current[key] ?? [];
      return {
        ...current,
        [key]: chosen.includes(viewId)
          ? chosen.filter((id) => id !== viewId)
          : [...chosen, viewId],
      };
    });
  }

  function move(key: string, index: number, delta: number) {
    setOrder((current) => {
      const chosen = [...(current[key] ?? [])];
      const target = index + delta;
      if (target < 0 || target >= chosen.length) return current;
      const [moved] = chosen.splice(index, 1);
      chosen.splice(target, 0, moved);
      return { ...current, [key]: chosen };
    });
  }

  function setRequest(format: string, patch: Partial<RequestDraft>) {
    setRequests((current) => ({
      ...current,
      [format]: { ...(current[format] ?? NO_REQUEST), ...patch },
    }));
  }

  if (load.phase === 'loading') {
    return (
      <div className="dj-dashboards">
        <Text as="p" color="secondary">Loading this knowledge base’s dashboards…</Text>
      </div>
    );
  }

  // A refusal is an ordinary answer, said out loud. src/api/dashboards.ts names
  // the endpoint that has to exist; repeating it here is what keeps a missing
  // surface from looking like an empty knowledge base.
  if (load.phase === 'error') {
    return (
      <div className="dj-dashboards">
        {/* A refusal is an ordinary answer — Banner is not used here because the
            page must not claim an alert role it has no reader waiting on; the
            sentence names the endpoint and that is the whole answer. */}
        <Text as="p" color="secondary">
          These dashboards could not be listed: {load.message}
        </Text>
      </div>
    );
  }

  const formats = load.data.formats;

  // render.ts: a KB with no dashboards publishes NO manifest — so there is
  // nothing to frame, and a frame around nothing is a worse answer than a
  // sentence.
  if (formats.length === 0) {
    return (
      <div className="dj-dashboards">
        <Text as="p" color="secondary">This knowledge base has no dashboards yet.</Text>
      </div>
    );
  }

  return (
    <div className="dj-dashboards">
      {formats.map((format) => (
        <FormatGroup
          key={format.format}
          org={org}
          kb={kb}
          format={format}
          canWrite={canWrite}
          open={open}
          order={order}
          request={requests[format.format] ?? NO_REQUEST}
          onToggleCompose={toggleCompose}
          onToggleView={toggleView}
          onMove={move}
          onSave={(composition, views) =>
            void client.saveComposition({
              org,
              kb,
              format: format.format,
              composition,
              views,
            })
          }
          onRequestChange={(patch) => setRequest(format.format, patch)}
          onRequestSubmit={(draft) => {
            onRequestView({
              org,
              kb,
              format: format.format,
              intent: draft.intent,
              audience: draft.audience,
            });
            setRequest(format.format, NO_REQUEST);
          }}
        />
      ))}
    </div>
  );
}

interface FormatGroupProps {
  org: string;
  kb: string;
  format: FormatDashboards;
  canWrite: boolean;
  open: Record<string, boolean>;
  order: Record<string, string[]>;
  request: RequestDraft;
  onToggleCompose: (format: string, composition: Composition) => void;
  onToggleView: (key: string, viewId: string) => void;
  onMove: (key: string, index: number, delta: number) => void;
  onSave: (composition: string, views: string[]) => void;
  onRequestChange: (patch: Partial<RequestDraft>) => void;
  onRequestSubmit: (draft: RequestDraft) => void;
}

/**
 * ONE FORMAT — a labelled region, because "grouped per format" is a structural
 * claim and a heading alone does not make it one. Its accessible name is the
 * format's CONTROL-PLANE name: the kb_record map key, which is also the name in
 * the URL and in the manifest.
 */
function FormatGroup(props: FormatGroupProps) {
  const { org, kb, format, canWrite, request } = props;
  const slug = format.format.replace(/[^a-zA-Z0-9_-]/g, '-');
  // E32-t5 — the PUBLISHED dashboard this format's named views anchor into.
  // The first composition holding a url is the served page; a format with
  // nothing published anchors nowhere, and the views list says so by not
  // linking (below) rather than by minting a url the server never spoke.
  const publishedUrl =
    format.compositions.find((composition) => composition.url !== null)?.url ?? null;
  // The format's own page inside the app: the published dashboard under the
  // shell, where its rows and its declared forms are (src/pages/DashboardView).
  // The composition below links to the ARTIFACT itself; this links to the app's
  // page for the same pair, so both readings of "open this format" work.
  const formatHref = `/${encodeURIComponent(org)}/${encodeURIComponent(kb)}/dashboards/${encodeURIComponent(format.format)}`;

  return (
    /* The <section> stays a real <section>: an aria-labelled section is what
       carries role="region", which is the structural claim dashboards.test.tsx
       reads ("grouped per format"). Card cannot render one — it has no `as` — so
       the section is the landmark and the Card is the surface inside it. */
    <section aria-label={format.format}>
      {/* E15-t5 — bordered, not floated; 7px and the 1px rule are the Card's own
          `--radius-container` and `--color-border`, which src/theme.ts already
          sets to the prototype's values. */}
      <Card padding={4} elevation="none">
        <Stack direction="vertical" gap={3}>
          <Stack direction="horizontal" gap={2} vAlign="center">
            {/* E15-t2 — the heading is the format's CONTROL-PLANE name (the
                kb_record map key, and the `:format` url segment), so it is set
                in the mono face through Link's `type`. `Heading level={2}` keeps
                the element and the outline; src/theme.ts puts h2 at the
                prototype's 13px card-header size, which is why a format name no
                longer arrives at 18px as though it were a page title. */}
            <Heading level={2}>
              <Link href={formatHref} type="code" color="inherit">
                {format.format}
              </Link>
            </Heading>
            {/* kb_record designates ONE format primary; the others carry no mark. */}
            {format.primary ? <Badge variant="info" label="Primary" /> : null}
            {canWrite ? (
              <span className="dj-format-request">
                <Button
                  type="button"
                  variant="secondary"
                  label="Request a view"
                  onClick={() => props.onRequestChange({ open: !request.open })}
                />
              </span>
            ) : null}
          </Stack>

          {/* E15-t5 — the divided header: the same rule the card's border is
              drawn in, run full-bleed to the card's edges. */}
          <Divider isFullBleed />

          {canWrite && request.open ? (
            <Card variant="muted" padding={3} elevation="none">
              <form
                onSubmit={(event) => {
                  event.preventDefault();
                  props.onRequestSubmit(request);
                }}
              >
                <Stack direction="vertical" gap={2} hAlign="start">
                  {/* Dashboard On Demand f1: the mission is the intent AND the
                      audience. The intent stays a <textarea> — a mission is
                      several sentences, TextInput is one line, and there is no
                      multi-line field in @astryxdesign/core to reach for. Its
                      label and id are kept so the association survives. */}
                  <label className="dj-field-label" htmlFor={`dj-intent-${slug}`}>
                    What should this view answer?
                  </label>
                  <textarea
                    id={`dj-intent-${slug}`}
                    className="dj-input"
                    rows={2}
                    value={request.intent}
                    onChange={(event) => props.onRequestChange({ intent: event.target.value })}
                  />
                  <TextInput
                    label="Who is it for?"
                    type="text"
                    value={request.audience}
                    onChange={(value) => props.onRequestChange({ audience: value })}
                    width="100%"
                  />
                  <Text as="p" type="supporting">
                    The agent drafts the pair and holds it for a maintainer to accept. Nothing is
                    published by asking.
                  </Text>
                  <Button type="submit" variant="primary" label="Send request" />
                </Stack>
              </form>
            </Card>
          ) : null}

          <ul className="dj-compositions" aria-label="Dashboards">
            {format.compositions.map((composition) => (
              <CompositionItem key={composition.id} {...props} composition={composition} />
            ))}
          </ul>

          {/* E32-t5 — THE SURFACE FAMILY (decision 0019): a format may own a
              REPORT beside its dashboard. It is deliberately NOT a composition
              row: a report has no status vocabulary, no commit of its own and
              no compose surface — its ceiling is capture for every role, so
              the whole truth is a badge naming the surface and the one act,
              opening it. The url is catalog.ts's own string (the reserved
              /report segment), never assembled here. A format without one
              renders nothing — absence is the answer, not a disabled row. */}
          {format.report ? (
            <div className="dj-report-row">
              <Badge label="Report" />
              <Link href={format.report.url} weight="semibold">
                Open report
              </Link>
            </div>
          ) : null}

          {/* E40-t2 — the FORM surface (the intake), on the report row's exact
              terms and for a sharper reason: this is the one published page a
              MEMBER is meant to act on, and until the listing named it the only
              way to reach a baked, served, guarded intake was to know the
              reserved segment and type it. Same shape as above — the host's own
              url, a badge naming the surface, one act — and nothing at all when
              the format owns no intake. */}
          {format.form ? (
            <div className="dj-report-row">
              <Badge label="Intake" />
              <Link href={format.form.url} weight="semibold">
                Open intake form
              </Link>
            </div>
          ) : null}

          {/* E32-t5 — NAMED VIEWS: the format's library (docujoint 0.14.0, one
              view per file under views/, the id IS the filename slug). Listed
              for EVERY role — the library is a read fact about the format, not
              the maintainer-only compose pool above, which stays the picker it
              was. Each view deep-links into the published dashboard at the
              engine's own anchor grammar (#/v/{vid}); with nothing published
              there is no page to anchor into, so the name is plain text — the
              same honesty as an unrendered composition's missing link. */}
          {format.storedViews.length > 0 ? (
            <>
              <Divider isFullBleed />
              <Text type="label" weight="semibold">
                Named views
              </Text>
              <ul className="dj-views" aria-label="Named views">
                {format.storedViews.map((view) => (
                  <li className="dj-view" key={view.id}>
                    {publishedUrl === null ? (
                      <Text weight="semibold">{view.title}</Text>
                    ) : (
                      <Link href={`${publishedUrl}#/v/${encodeURIComponent(view.id)}`}>
                        {view.title}
                      </Link>
                    )}
                    {/* the SOURCE FILE, mono — a view's identity is its file
                        (catalog.ts), and a file path is a machine fact */}
                    {view.file ? (
                      <Text type="code" color="secondary">
                        {view.file}
                      </Text>
                    ) : null}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
        </Stack>
      </Card>
    </section>
  );
}

/** ONE COMPOSITION — its status, its stable link, and (for a maintainer) the
 *  surface that picks and orders the views it is made of. */
function CompositionItem(props: FormatGroupProps & { composition: Composition }) {
  const { format, composition, canWrite } = props;
  const key = keyOf(format.format, composition.id);
  const isOpen = canWrite && props.open[key] === true;
  const chosen = props.order[key] ?? composition.views;
  const titleOf = (id: string) => format.storedViews.find((view) => view.id === id)?.title ?? id;

  return (
    <li className="dj-composition">
      <div className="dj-composition-head">
        {/* serve.ts serves the published artifact itself, so a rendered pair is
            a LINK out of the app; a pair with nothing published is plain text,
            because there is nothing to open. */}
        {composition.url === null ? (
          <Text weight="semibold">{composition.title}</Text>
        ) : (
          <Link href={composition.url} weight="semibold">
            {composition.title}
          </Link>
        )}
        {/* E15-t3 — the render status, as a chip. STATUS_LABEL is unchanged:
            "Not rendered yet" is a phrase somebody can act on and the chip is
            its form, not its replacement. */}
        <Chip state={STATUS_CHIP[composition.status]}>{STATUS_LABEL[composition.status]}</Chip>
        {/* E15-t2 — A COMMIT AND A DATE ARE THE CANONICAL MACHINE FACTS, and
            `hasTabularNumbers` was the app reaching for half of what it wanted:
            tabular figures in a proportional face still leave the letters of an
            oid ragged. `type="code"` is the whole answer — the mono stack from
            src/theme.ts at the prototype's 12.5 metadata size — so it replaces
            the flag rather than joining it. */}
        {composition.commit === null ? null : (
          <Text type="code" color="secondary">
            commit {composition.commit}
          </Text>
        )}
        {composition.renderedAt === null ? null : (
          <Text as="span" type="code" color="secondary">
            <time dateTime={composition.renderedAt}>{isoDay(composition.renderedAt)}</time>
          </Text>
        )}
        {canWrite ? (
          <span className="dj-composition-action">
            <Button
              type="button"
              variant="secondary"
              label="Compose"
              aria-expanded={isOpen}
              onClick={() => props.onToggleCompose(format.format, composition)}
            />
          </span>
        ) : null}
      </div>

      {isOpen ? (
        <div className="dj-compose">
          {/* 0015: reuse follows the FORMAT — the pool is this format's stored
              views and no other format's. */}
          <fieldset className="dj-pool">
            <legend>Stored views</legend>
            {format.storedViews.map((view) => (
              <label className="dj-pool-option" key={view.id}>
                <input
                  type="checkbox"
                  checked={chosen.includes(view.id)}
                  onChange={() => props.onToggleView(key, view.id)}
                />
                {view.title}
              </label>
            ))}
          </fieldset>

          {/* the composition IS this order — 0015: a versioned list of view
              references, top to bottom. */}
          <ul className="dj-order" aria-label="Composition order">
            {chosen.map((viewId, index) => (
              <li className="dj-order-item" key={viewId}>
                <span className="dj-order-title">{titleOf(viewId)}</span>
                <Button
                  type="button"
                  variant="ghost"
                  label="Move up"
                  onClick={() => props.onMove(key, index, -1)}
                />
                <Button
                  type="button"
                  variant="ghost"
                  label="Move down"
                  onClick={() => props.onMove(key, index, 1)}
                />
              </li>
            ))}
          </ul>

          <Button
            type="button"
            variant="primary"
            label="Save composition"
            onClick={() => props.onSave(composition.id, chosen)}
          />
        </div>
      ) : null}
    </li>
  );
}
